// Apple Ads attribution, app side. See server/attribution.mjs for the why.
//
// ONE JOB: on the first launch after an iOS install, ask AdServices for its
// one-off token, hand it to our server under a random id made up here, and
// remember the server's yes/no. Later the billing code says "a trial started"
// and that is sent under the same id, if (and only if) Apple attributed the
// install to an ad.
//
// WHAT IT NEVER DOES. No IDFA, no ATT prompt, no third-party SDK. Nothing on
// Android, nothing on the web, nothing in the demo. A token call that fails, a
// server that is down or a server that has the feature off all end the same
// way: nothing happens, and the app is none the wiser.
//
// The state is three small values in the app's own storage:
//   installId   random, made up for this and used for nothing else
//   state       'pending' (not yet answered) | 'done' (answered, or given up)
//   attributed  true when Apple said an ad brought this install
//   tries       launches spent on it, so a flaky network cannot make it loop

export const KEY = 'attribution';
/** Launches it may take to get one clean answer, then it stops for good. */
export const MAX_TRIES = 3;

const ID_SHAPE = /^[a-z0-9-]{16,64}$/;

function newInstallId() {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function createAttribution({
  getPlugin = () => null,
  platform = 'web',
  apiBase = '',
  fetchImpl = typeof fetch === 'function' ? fetch : null,
  store = {},
  makeId = newInstallId,
} = {}) {
  const read = () => {
    const raw = store[KEY];
    return raw && typeof raw === 'object' ? raw : {};
  };
  const write = (patch) => { store[KEY] = { ...read(), ...patch }; };

  /** Only an iOS app build, with the plugin there, can do this at all. */
  const supported = () => platform === 'ios' && !!getPlugin();

  async function post(path, body) {
    if (!fetchImpl) return null;
    try {
      const res = await fetchImpl(`${apiBase}/api/attribution/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  /**
   * The once-only check. Resolves a short word for tests and logs:
   * 'unsupported' | 'done' | 'no-token' | 'unavailable' | 'disabled' |
   * 'attributed' | 'organic'. NEVER THROWS.
   */
  async function start() {
    try {
      if (!supported()) return 'unsupported';
      const cur = read();
      if (cur.state === 'done') return 'done';
      const tries = Number(cur.tries) || 0;
      if (tries >= MAX_TRIES) { write({ state: 'done' }); return 'done'; }

      const installId = ID_SHAPE.test(String(cur.installId ?? '')) ? cur.installId : makeId();
      write({ installId, state: 'pending', tries: tries + 1 });

      let token = null;
      try {
        const out = await getPlugin().token();
        token = typeof out?.token === 'string' && out.token ? out.token : null;
      } catch { /* iOS < 14.3, or Apple would not say */ }
      // No token is final: it will not appear on a later launch either.
      if (!token) { write({ state: 'done' }); return 'no-token'; }

      const answer = await post('install', { installId, token });
      if (!answer) return 'unavailable';          // network or server: next launch
      if (answer.enabled === false) { write({ state: 'done' }); return 'disabled'; }
      if (answer.ok !== true) return 'unavailable';
      write({ state: 'done', attributed: answer.attributed === true });
      return answer.attributed === true ? 'attributed' : 'organic';
    } catch {
      return 'unavailable';
    }
  }

  /**
   * `trial_started` or `subscribed`, against this install. Sent only for an
   * install Apple attributed to an ad; for everybody else it is a no-op, so an
   * organic user's purchase never leaves the phone as anything but the purchase.
   */
  async function event(name) {
    try {
      const cur = read();
      if (!supported() || cur.attributed !== true || !ID_SHAPE.test(String(cur.installId ?? ''))) return false;
      const answer = await post('event', { installId: cur.installId, event: name });
      return answer?.ok === true;
    } catch {
      return false;
    }
  }

  return { start, event, supported };
}

// ── The app's instance ───────────────────────────────────────────────────────

import { API_BASE, isNative, platform as nativePlatform, plugin as nativePlugin } from './core/native.js';
import { prefs } from './core/storage.js';
import { isDemo } from './core/demo.js';

export const attribution = createAttribution({
  // Bound methods, never the proxy: awaiting a Capacitor plugin proxy hangs.
  getPlugin: () => {
    if (!isNative || isDemo.value) return null;
    const p = nativePlugin('Attribution');
    return p ? { token: () => p.token() } : null;
  },
  platform: nativePlatform,
  apiBase: API_BASE,
  store: prefs,
});
